import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { Kicker, MaskLines, Rise, SlideShell } from "@/deck/ui";

const CITIES = ["Medan", "Riau", "Batam", "Bengkulu", "Bandung", "Jakarta", "Cipali", "Karawang", "Majalengka", "Semarang", "Solo", "DIY", "Surabaya", "Malang", "Sidoarjo", "Kediri", "Probolinggo", "Madura", "Pontianak", "Banjarmasin", "Manado", "Gorontalo", "Makassar", "Bali"];

const PINS = [[85, 30], [175, 100], [204, 113], [371, 128], [195, 240], [301, 297], [323, 300], [345, 307], [393, 320], [405, 331], [428, 331], [448, 327], [472, 341], [526, 357], [513, 228], [641, 280], [737, 125], [784, 95]];

const STATS = [
  { v: "24", l: "Cities" },
  { v: "8", l: "Segments" },
  { v: "5+", l: "Years with clients" },
  { v: "2020", l: "Live since" },
];

const QUOTES = [
  { q: "…it has really helped our operational efficiency. All transaction data is monitored via webreport…", who: "Mrs. Hen", role: "Mall Manager · Surabaya", img: "/assets/testimoni-1.png" },
  { q: "…helping to simplify the management of electricity consumption with the large number of rooms available.", who: "Kurnia", role: "Guesthouse Owner · Solo", img: "/assets/testimoni-2.png" },
  { q: "…to manage underground electricity with one main power source in premium housing…", who: "Aji", role: "Property Developer · DIY", img: "/assets/testimoni-3.png" },
];

export default function Traction() {
  return (
    <div className="relative min-h-full lg:h-full">
      <SlideShell testId="traction-slide" className="lg:pb-36">
        <div className="grid flex-1 gap-6 lg:min-h-0 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Rise><Kicker index="07">Traction</Kicker></Rise>
            <MaskLines lines={["Already trusted", "across Indonesia."]} accentIndex={1} className="mt-4 text-4xl font-extrabold leading-[0.95] tracking-[-0.035em] sm:text-5xl lg:text-6xl" />
            <div className="mt-7 grid grid-cols-4 gap-2" data-testid="traction-stats">
              {STATS.map((s, i) => (
                <Rise key={s.l} delay={0.35 + i * 0.08} className="border-l border-white/10 pl-3">
                  <p className="font-display text-3xl font-extrabold tracking-tight text-zinc-50 lg:text-4xl">{s.v}</p>
                  <p className="mt-1 text-[11px] leading-tight text-zinc-400">{s.l}</p>
                </Rise>
              ))}
            </div>
            <Rise delay={0.6} className="mt-6 flex items-center gap-3">
              <span className="font-mono-d text-[11px] uppercase tracking-[0.18em] text-zinc-400">Regulated & certified</span>
              <img src="/assets/reg1.png" alt="Metrology & Kominfo certification" className="h-8 rounded bg-white px-1" />
              <img src="/assets/reg2.png" alt="OSS / BKPM registration" className="h-8 rounded bg-white px-1" />
            </Rise>
            <p className="mt-4 font-mono-d text-[11px] text-zinc-500">Source: powermanager.my.id — TOKENKU Power Manager</p>
            <Rise delay={0.75} className="mt-5 grid grid-cols-3 gap-2" data-testid="traction-field-photos">
              {["gal-3", "gal-1", "gal-6"].map((g) => (
                <div key={g} className="group relative h-24 overflow-hidden rounded-xl border border-white/10 lg:h-28">
                  <img src={`/assets/${g}.png`} alt="Power Manager field installation" className="h-full w-full object-cover grayscale transition-[filter,transform] duration-700 group-hover:scale-105 group-hover:grayscale-0" />
                </div>
              ))}
            </Rise>
          </div>
          <div className="flex flex-col gap-4 lg:col-span-7">
            <Rise delay={0.25} className="relative w-full" data-testid="traction-map">
              <img src="/assets/Frame-2321.png" alt="Map of Indonesian cities using Power Manager" className="w-full opacity-60 [filter:invert(1)_brightness(0.35)_contrast(1.2)]" />
              {PINS.map(([x, y], i) => (
                <motion.span
                  key={i}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.5 + i * 0.05, type: "spring", stiffness: 260, damping: 16 }}
                  className="absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--dash)] shadow-[0_0_12px_var(--dash)]"
                  style={{ left: `${(x / 1200) * 100}%`, top: `${(y / 450) * 100}%` }}
                >
                  <span className="pulse-ring absolute inset-0 rounded-full bg-[var(--dash)]" style={{ animationDelay: `${i * 0.13}s` }} />
                </motion.span>
              ))}
            </Rise>
            <div className="grid gap-3 sm:grid-cols-3">
              {QUOTES.map((t, i) => (
                <Rise key={t.who} delay={0.6 + i * 0.08} className="rounded-2xl border border-white/10 bg-[var(--panel)] p-4" data-testid={`traction-testimonial-${i + 1}`}>
                  <Quote size={16} className="text-[var(--dash)]" />
                  <p className="mt-2 text-xs leading-relaxed text-zinc-200">{t.q}</p>
                  <div className="mt-3 flex items-center gap-2">
                    <img src={t.img} alt={t.who} className="h-7 w-7 rounded-full object-cover" />
                    <div>
                      <p className="text-xs font-semibold text-zinc-50">{t.who}</p>
                      <p className="text-[11px] text-zinc-400">{t.role}</p>
                    </div>
                  </div>
                </Rise>
              ))}
            </div>
          </div>
        </div>
      </SlideShell>
      <div className="relative overflow-hidden border-y border-white/10 py-3 lg:absolute lg:inset-x-0 lg:bottom-[76px]" data-testid="traction-city-marquee">
        <div className="marquee-track">
          {[...CITIES, ...CITIES].map((c, i) => (
            <span key={i} className="flex items-center font-display text-2xl font-semibold tracking-tight text-zinc-500 lg:text-3xl">
              <span className={i % 3 === 0 ? "text-zinc-100" : ""}>{c}</span>
              <span className="mx-6 h-1.5 w-1.5 rounded-full bg-[var(--dash)]" />
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
